import { md5 } from "./md5.ts";
import { record } from "rrweb";
import { getRecordConsolePlugin } from "@rrweb/rrweb-plugin-console-record";
import pako from "pako";

import {
  decode,
  encode,
  executeBeforeUnload,
  executeIfDOMReady,
  formatDatetime,
  getAppMode,
  getCleanedProperties,
  getDefaultSessionProperties,
  getDefaultUserProperties,
  getDeviceInfo,
  getJSONOrNull,
  getPageTitle,
  isDefined,
  isNotDefined,
  isNumber,
  isObject,
  isString,
  millisToSeconds,
  toDecimalPoints,
  unregisterBeforeUnloadCallback,
  uuid,
  stringToArrayBuffer,
  arrayBufferToString,
  ignoreClientEventProps,
  ignoreUserProps
  // ignoreSessionProps,
} from "./utils.ts";
import EventHandler from "./EventHandler.ts";
import {
  APP_EVENT,
  KLARITICS_APP_OPENED,
  KLARITICS_CONFIG_HASH,
  KLARITICS_LAST_CONFIG_TIME,
  KLARITICS_LAST_ENABLED_FLAG,
  KLARITICS_LAST_ENABLE_DISABLE_TIME,
  KLARITICS_LAST_EVENT_TIME,
  KLARITICS_PAGE_OPENED,
  KLARITICS_SESSION_ID,
  KLARITICS_USER_ATTR_KEY,
  KLARITICS_USER_INFO_HASH,
  CLIENT_EVENT,
  NAVIGATION_EVENT,
  SYSTEM_EVENT,
  KLARITICS_LT_COUNT,
  VISIBLE,
  REATINED_SESSIONS,
  KLARITICS_SESSION_START_TIME,
  klariticsKeys,
  KLARITICS_EVENTS_LOGGED,
  LOCAL_STORAGE
  // DEFAULT_CONFIG
} from "./constants.ts";
import {
  DEFAULT_CHUNK_DETAILS,
  getSdkInfo,
  makeEvent,
  makeAppInfo,
  makeDeviceInfo,
  makeNavigationEvent,
  makeSystemEvent,
  getGeoLocation
} from "./models.ts";
import API from "./API.ts";
import Logger from "./Logger.ts";
import SessionHandler from "./Session.ts";
import DataManager from "./DataManager.ts";
import { Storage } from "./Storage.ts";
import SystemEventMonitor from "./SystemEventMonitor.ts";
import type {
  ControllerInterface,
  AppInfo,
  SDKInfo,
  DeviceInfo,
  SessionInfo,
  Chunk,
  ChunkDetails,
  NavigationEvent,
  EventsLoggedMap,
  LtCountMap,
  ClientConfig,
  InitOptions,
  FieldsObject,
  mergedInfo,
  Details,
  ChunkMeta,
  AppEvent,
  SystemEvent,
  userDataPayload,
  ENABLED_STATUS_API_RESPONSE,
  ReplayEvent,
  XHRWithNetworkDetails
} from "./types/types.ts";

const errorFunction =
  (funcName = "") =>
  (): void => {
    Logger.error(`${funcName}: Klaritics SDK not initialised.`);
  };

export const DefaultController = {
  reportChunkOnVisibilityChange: errorFunction("reportChunkOnVisibilityChange"),
  logAppEvent: errorFunction("logAppEvent"),
  logClientEvent: errorFunction("logClientEvent"),
  logPageView: errorFunction("logPageView"),
  setUserId: errorFunction("setUserId"),
  setUserProperties: errorFunction("setUserProperties"),
  setSessionProperties: errorFunction("setSessionProperties"),
  setAppVersion: errorFunction("setAppVersion"),
  getClientId: errorFunction("getClientId"),
  getSessionId: errorFunction("getSessionId"),
  startSession: errorFunction("startSession"),
  endSession: errorFunction("endSession"),
  flattenJSON: errorFunction("flattenJSON"),
  setRedirectionHandler: errorFunction("setRedirectionHandler"),
  setInitFlag: errorFunction("setInitFlag"),
  dispatchValidationEvent: errorFunction("dispatchValidationEvent"),
  resetUserProperties: errorFunction("resetUserProperties"),
  resetSessionProperties: errorFunction("resetSessionProperties"),
  clearLocalStorage: errorFunction("clearLocalStorage"),
  getDeviceId: errorFunction("getDeviceId"),
  startNewSession: errorFunction("startNewSession"),
  getSiteId: errorFunction("getSiteId"),
  setKlariticsEventListener: errorFunction("setKlariticsEventListener")
};

export default class Controller implements ControllerInterface {
  public static instance: Controller | null = null;

  // Instance Variables with proper initialization or type guards
  _eventHandler: EventHandler = new EventHandler();
  _isInitialized: boolean = false;
  _appInfo: AppInfo = {};
  _sdkInfo: SDKInfo = {};
  _deviceInfo: DeviceInfo = {};
  _sessionInfo: SessionInfo = {};
  _userAttributes: Record<string, unknown> = {};
  _sessionAttributes: Record<string, unknown> = {};
  _chunkDetails: ChunkDetails = { ...DEFAULT_CHUNK_DETAILS };
  _chunkNumber: number = 1;
  _currentNavigation: NavigationEvent | null = null;
  _isRunning: boolean = false;
  _isEventMonitorRegistered: boolean = false;
  _dataManager!: DataManager;
  _customEventListener: unknown = null;
  _systemEventMonitor!: SystemEventMonitor;
  _sessionHandler!: SessionHandler;
  _idleTimeout: number = 3600;
  _maxEventsSentCount: number = 20;
  _chunkSendInterval: number = -1;
  _internalChunkSendInterval: number = 20000;
  _chunk502RetryTime: number = -1;
  _maxSessionsInStorage: number = 10;
  _isSending: boolean = false;
  _isFirstSession: boolean = false;
  _last502Time: number = -1;
  _batchEvents: boolean = false;
  _bulkUpload: boolean = true;
  clientEvents: AppEvent[] = [];
  _stopEventsFromConfig: string[] = [];
  _appEventsFromConfig: string[] = [];
  _stopEventPropsFromConfig: string[] = [];
  _stopSpecificEventPropsFromConfig: Record<string, string[]> = {};
  _stopUserPropsFromConfig: string[] | undefined = [];
  _data: Record<string, unknown> = {};
  _sessionCount: number = 0;
  _redirectionHandler: ((url: string) => void) | null = null;
  _sendChunkOnEventsId: ReturnType<typeof setInterval> | null = null;
  _sendChunkOnETAId: ReturnType<typeof setInterval> | null = null;
  _blockChunkReporting: boolean = false;
  _SDKStartedAfterPluginInit: boolean = false;
  _eventsLogged: EventsLoggedMap = {};
  initWaitTime: number = 1500;
  version: string = "";
  _deviceId: string = "";
  _configHash: string = "";
  sessionStartTime: number | null = null;
  sessionId: string = "";
  _ltCountObj: LtCountMap = {};
  _pendingChunk: boolean = false;
  _ignoredClientEvents: Record<string, unknown> | null = null;

  window!: Window;
  startTime!: number;
  siteId!: string;
  host!: string;
  clientConfig: Partial<ClientConfig> = {};
  config: Partial<ClientConfig> = {};
  newSession?: boolean;
  _api!: API;

  private _videoEventsBuffer: ReplayEvent[] = [];
  private _stopVideoRecordingFn: (() => void) | undefined | null = null;

  constructor() {
    if (Controller.instance) {
      return Controller.instance;
    }
    Controller.instance = this;
  }

  public static getInstance(): Controller {
    if (!Controller.instance) {
      Controller.instance = new Controller();
    }
    return Controller.instance;
  }

  init(
    siteId: string,
    host: string,
    options?: ClientConfig | InitOptions,
    success: () => void = () => {},
    error: () => void = () => {}
  ): void {
    if (this._isInitialized) {
      success();
      return;
    }

    const clientConfig = { ...(options as unknown as ClientConfig) };
    const windowObj: Window =
      typeof window !== "undefined"
        ? window
        : (globalThis as unknown as Window);
    const startTime = Date.now();

    try {
      this.initialize(
        siteId,
        host,
        startTime,
        clientConfig,
        windowObj,
        success,
        error
      );
    } catch (e) {
      Logger.error(`Controller : init failed ${e}`);
      error();
    }
  }

  // Uses arrow function to preserve 'this' context for callbacks
  public initialize = (
    siteId: string,
    host: string,
    startTime: number,
    clientConfig: ClientConfig,
    windowRef: Window,
    success_callback: () => void,
    error_callback: () => void
  ): void => {
    if (this._isInitialized) return;

    const onBrowserStorageLoad = (): void => {
      Logger.debug("Controller : Wait for browser storage completed.");
      this._initializeStateVariables(
        siteId,
        host,
        startTime,
        clientConfig,
        windowRef
      );
      this._setConfig();
      this._initializeFieldsFromConfig();
      this._checkEnableStatusInConfig(onEnableConfig, error_callback);
    };

    const onEnableConfig = (): void => {
      Logger.debug("Controller : On Site enabled in config");
      this._getConfigHash(onConfigHashFetch, error_callback);
    };

    const onConfigHashFetch = (configHash: string): void => {
      this._configHash = configHash;
      this._waitForResourceLoad(configHash, onResourceWaitComplete);
    };

    const onResourceWaitComplete = (): void => {
      Logger.debug("Controller : Waiting for resources completed");
      this._initializeLTCount();
      this._updateAppInfo();
      this._deviceId = this._getDeviceId();
      this._setDeviceInfo();
      this._setDefaultUserProperties();
      this._mergeStoredAndDefaultUserAttributes();
      this._postUserInfo();
      this._getStoredListOfEventsLogged();
      this._setDefaultSessionProperties();
      this._setInitialSessionState();
      this._reinitialiseChunkDetails();
      this._downloadConfig(this._configHash, () => {
        Logger.debug("Controller : Config loaded, starting reporting engine.");
        const totalBgTime = this._getTotalBackgroundTime();
        this._sessionHandler = new SessionHandler(totalBgTime);
        this._registerSystemEventMonitor();
        this.sendOldChunk(onOldChunkReport);
      });
    };

    const onOldChunkReport = (): void => {
      this._isRunning = true;
      this._isInitialized = true;
      const chunk = this.createANewChunk();
      this._dataManager.persistCurrentChunk(chunk);
      onPluginInit();
    };

    const onPluginInit = (): void => {
      this._dispatchSystemEvent(this.window.document.visibilityState);
      // INITIALIZE REPLAY OBSERVERS & HOOKS
      // this._startVideoSessionRecording();
      // this._initNetworkInterceptor();
      // this._initNavigationInterceptor();
      // this._initGlobalErrorInterceptor();
      if (this.newSession) {
        this.sessionStartTime = Date.now();
        this.logAppEvent(
          KLARITICS_APP_OPENED,
          { time: formatDatetime(new Date()) },
          undefined,
          false,
          "klaritics"
        );
      }
      this.logPageView(this.window.location.pathname);
      this._sendChunkWithAtleastOneEvent();
      this._registerIntervalBasedChunkReporting();
      success_callback();
    };

    this._waitForBrowserStorage(onBrowserStorageLoad);
  };

  getSiteId = (): string => this.siteId;
  getDevInfo = (): DeviceInfo => this._deviceInfo;
  getListOfEventsLogged = (): EventsLoggedMap => this._eventsLogged;
  getSessionId = (): string => this.sessionId;
  getSessionInfo = (): SessionInfo => this._sessionInfo;
  getUserAttributes = (): Record<string, unknown> => this._userAttributes;
  getSessionAttributes = (): Record<string, unknown> => this._sessionAttributes;

  getClientId = (): string | null | undefined => {
    if (!this._isInitialized) {
      DefaultController.getClientId();
      return null;
    }
    return this._deviceId;
  };

  getInstallationTime = (): number => {
    return (this._dataManager as DataManager).getInstallationTime(
      this.startTime
    );
  };

  logEvent = (
    name: string,
    properties: FieldsObject = {},
    forceReport: boolean = false,
    loggedBy: string = "customer"
  ): void => {
    this.logAppEvent(name, properties, undefined, forceReport, loggedBy);
  };

  logAppEvent = (
    name: string,
    additionalInfo: Record<string, unknown> = {},
    category?: string,
    forceReport: boolean = false,
    loggedBy: string = "customer"
  ): void => {
    if (!this._isInitialized || !this._isRunning) {
      Logger.warn("Controller : Can't log App event. SDK not initialised");
      return;
    }

    try {
      if (isString(name) && isObject(additionalInfo)) {
        const evName = name.replace(/'/g, "");
        this._incrementLtCount(evName);

        const lifetimecount = this._ltCountObj[evName] ?? 0;
        let mergedInfo: mergedInfo = {
          ...additionalInfo,
          $lt_count: lifetimecount
        };

        this._updateListOfEventsLogged(name, mergedInfo);

        mergedInfo = {
          ...mergedInfo,
          first_logged_time: this._eventsLogged[name]?.first_logged_time,
          latest_logged_time: this._eventsLogged[name]?.latest_logged_time
        };

        const cleanedInfo = ignoreClientEventProps(
          name,
          mergedInfo,
          this._stopEventPropsFromConfig,
          this._stopSpecificEventPropsFromConfig
        );

        const event = makeEvent(
          name,
          getCleanedProperties(cleanedInfo),
          category,
          this.getTimeElapsedInSession()
        );

        if (!event) {
          Logger.debug(`Controller : Invalid app event ${name}`);
          return;
        }

        let type: string = APP_EVENT;
        if (this._stopEventsFromConfig.includes(name)) return;

        this.dispatchEvent(type, event, false, forceReport, loggedBy);
      }
    } catch (e) {
      Logger.error(`Event logging failed: ${e}`);
    }
  };

  logClientEvent = (
    name: string,
    additionalInfo?: Record<string, unknown>
  ): void => {
    if (!this._isInitialized || !this._isRunning) {
      Logger.warn("Controller : Can't log client event. SDK not initialised");
      DefaultController.logClientEvent();
      return;
    }

    const lifetimecount = (this._dataManager as DataManager).getltCount(name);
    const mergedInfo = { ...additionalInfo, klaritics_lt_count: lifetimecount };
    const event = makeEvent(
      name,
      getCleanedProperties(mergedInfo),
      undefined,
      this.getTimeElapsedInSession()
    );

    let type: string = CLIENT_EVENT;
    if (this._stopEventsFromConfig.includes(name)) return;
    else if (this._appEventsFromConfig.includes(name)) type = APP_EVENT;

    if (event) {
      Logger.debug(`Controller : Logging client event ${name}`);
      this._updateListOfEventsLogged(name, mergedInfo);
      this.dispatchEvent(type, event);
    } else {
      Logger.debug(`Controller : Invalid client event ${name}`);
    }
  };

  /**
   * @function    _initializeBlackListEvents
   * @description Initializes the blacklist props and events to the class
   * @private
   */
  _initializeBlackListProperties = (blackListConfig: Details | undefined) => {
    Logger.debug("Controller: Initializing blacklist Properties");
    const config = blackListConfig || this._dataManager.getExistingConfig();
    const blacklist = config?.blacklisting;
    this._stopEventsFromConfig = blacklist?.events ?? [];
    this._stopEventPropsFromConfig = blacklist?.shared_event_properties ?? [];
    this._stopSpecificEventPropsFromConfig =
      blacklist?.specific_event_properties ?? {};
    this._stopUserPropsFromConfig = blacklist?.user_properties;
  };

  /**
   * @function    _initGlobalErrorInterceptor
   * @description To Record Runtime errros
   * @private
   */
  private _initGlobalErrorInterceptor = (): void => {
    const root = this;

    this.window.addEventListener("error", (event) => {
      root._videoEventsBuffer.push({
        type: 6,
        data: {
          plugin: "runtime-error",
          payload: {
            message: event.message,
            source: event.filename,
            line: event.lineno,
            column: event.colno,
            stack: event.error?.stack || "No stack trace available"
          }
        },
        timestamp: Date.now()
      });
    });

    // Track unhandled Promise rejections (e.g., failed API calls that were not caught)
    this.window.addEventListener("unhandledrejection", (event) => {
      root._videoEventsBuffer.push({
        type: 6,
        data: {
          plugin: "promise-rejection",
          payload: {
            reason: String(event.reason?.message || event.reason)
          }
        },
        timestamp: Date.now()
      });
    });
  };

  /**
   * @function    _startVideoSessionRecording
   * @description Instantiates internal MutationObservers, click hooks, and console plugins
   * @private
   */
  private _startVideoSessionRecording = (): void => {
    if (this._stopVideoRecordingFn) return;

    try {
      Logger.debug(
        "Controller : Initializing mutation observers and user element logging."
      );

      this._stopVideoRecordingFn = record({
        emit: (event) => {
          this._videoEventsBuffer.push(event);
        },
        recordCanvas: true,
        ignoreClass: "klaritics-ignore",
        inlineStylesheet: true,
        sampling: {
          mousemove: 150,
          scroll: 150
        },
        maskAllInputs: true,
        plugins: [
          getRecordConsolePlugin({
            level: ["log", "info", "warn", "error"],
            stringifyOptions: {
              numOfKeysLimit: 3,
              depthOfLimit: 3
            }
          })
        ]
      });
    } catch (e) {
      Logger.error("Controller : Exception launching session capture layer", e);
    }
  };

  /**
   * @function _initNetworkInterceptor
   * @description Intercepts Window.fetch and XMLHttpRequest pipelines to gather performance logs
   */
  private _initNetworkInterceptor = (): void => {
    const root = this;

    // FETCH WIRE HOOK
    const originalFetch = this.window.fetch;
    this.window.fetch = async function (...args) {
      const startTime = Date.now();
      const url =
        typeof args[0] === "string" ? args[0] : (args[0] as Request).url;
      const method = args[1]?.method || "GET";

      try {
        const response = await originalFetch.apply(this, args);
        root._videoEventsBuffer.push({
          type: 6,
          data: {
            plugin: "network",
            payload: {
              type: "fetch",
              url,
              method,
              status: response.status,
              duration: Date.now() - startTime
            }
          },
          timestamp: Date.now()
        });
        return response;
      } catch (err) {
        root._videoEventsBuffer.push({
          type: 6,
          data: {
            plugin: "network",
            payload: {
              type: "fetch",
              url,
              method,
              status: "FAILED",
              duration: Date.now() - startTime,
              error: String(err)
            }
          },
          timestamp: Date.now()
        });
        throw err;
      }
    };

    // XMLHTTPREQUEST (XHR) WIRE HOOK
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      (this as XHRWithNetworkDetails)._networkDetails = {
        method,
        url: String(url),
        startTime: Date.now()
      };
      return originalOpen.apply(this, [method, url, ...rest] as Parameters<
        typeof originalOpen
      >);
    };

    XMLHttpRequest.prototype.send = function (body) {
      this.addEventListener("loadend", () => {
        const xhrDetails = (this as XHRWithNetworkDetails)._networkDetails;
        if (xhrDetails) {
          root._videoEventsBuffer.push({
            type: 6,
            data: {
              plugin: "network",
              payload: {
                type: "xhr",
                url: xhrDetails.url,
                method: xhrDetails.method,
                status: this.status,
                duration: Date.now() - xhrDetails.startTime
              }
            },
            timestamp: Date.now()
          });
        }
      });
      return originalSend.apply(this, [body]);
    };
  };

  /**
   * @function _initNavigationInterceptor
   * @description Watches dynamic pushState loops to handle SPAs smoothly
   */
  private _initNavigationInterceptor = (): void => {
    const root = this;
    const originalPushState = this.window.history.pushState;

    this.window.history.pushState = function (...args) {
      originalPushState.apply(this, args);
      root.logPageView(root.window.location.pathname);

      root._videoEventsBuffer.push({
        type: 6,
        data: {
          plugin: "navigation",
          payload: {
            path: root.window.location.pathname,
            title: root.window.document.title
          }
        },
        timestamp: Date.now()
      });
    };
  };

  /**
   * @description Compresses the recorded event queue into a binary packet and transmits it
   */
  public flushAndReportVideoChunk = (): void => {
    if (!this._isInitialized || this._videoEventsBuffer.length === 0) return;

    try {
      const sessionId = this.sessionId;
      const payloadString = JSON.stringify(this._videoEventsBuffer);

      // const eventsToPublish = [...this._videoEventsBuffer];
      this._videoEventsBuffer = [];

      const compressedBinary = pako.gzip(payloadString);
      const binaryBlob = new Blob([compressedBinary], {
        type: "application/octet-stream"
      });

      const formData = new FormData();
      formData.append("sessionId", sessionId);
      formData.append("videoChunk", binaryBlob);

      this._api.postCompressedVideoChunk(
        formData,
        () => {
          Logger.debug(
            "Controller: Binary telemetry record packet sent cleanly."
          );
        },
        (err) => {
          Logger.error(
            "Controller : Recording payload transmission failed",
            err
          );
        }
      );
    } catch (err) {
      Logger.error("Controller : Failed to compress stream block", err);
    }
  };

  setRedirectionHandler = (callback: (...args: unknown[]) => void): void => {
    if (!this._isInitialized) {
      DefaultController.setRedirectionHandler();
      return;
    }
    if (!this._redirectionHandler) {
      this._redirectionHandler = callback;
      this.registerForEvent(
        "CLIENT_EVENT",
        this._clientRedirectionHandler as (...args: unknown[]) => void
      );
    } else {
      this._redirectionHandler = callback;
    }
  };

  private _clientRedirectionHandler = (
    event: AppEvent & { additional_info?: { url: string } }
  ) => {
    if (event && event.name === "REDIRECT") {
      this._redirectionHandler!(event.additional_info!.url);
    }
  };

  setKlariticsEventListener = (AEL: unknown): void => {
    this._customEventListener = AEL;
  };

  registerForEvent = (
    type: string,
    callback: (...args: unknown[]) => void
  ): void => {
    (this._eventHandler as EventHandler).registerForEvent(type, callback);
  };

  logPageView = (
    pathname: string,
    time: number = this.getTimeElapsedInSession()
  ): void => {
    if (!this._isInitialized || !this._isRunning) {
      DefaultController.logPageView();
      return;
    }

    if (pathname && pathname.length > 0) {
      const transitionTime = toDecimalPoints(time);
      const navigation = makeNavigationEvent(
        pathname,
        getPageTitle(this.window),
        0,
        transitionTime
      );

      if (!this._currentNavigation) {
        this._currentNavigation = navigation;
      } else {
        this._endNavigation();
        this._currentNavigation = navigation;
      }

      this.logAppEvent(
        KLARITICS_PAGE_OPENED,
        { time: formatDatetime(new Date()), navigation_id: pathname },
        undefined,
        false,
        "klaritics"
      );
    }
  };

  // ── Internal Helpers (Private) ─────────────────────────────────────────────

  private _initializeStateVariables(
    siteId: string,
    host: string,
    startTime: number,
    clientConfig: ClientConfig,
    windowRef: Window
  ): void {
    this.window = windowRef;
    this.startTime = startTime;
    this.siteId = siteId;
    this.host = host;
    this.clientConfig = clientConfig || {};
    this.initWaitTime = this.clientConfig.sdk_init_wait
      ? this.clientConfig.sdk_init_wait * 1000
      : 2000;

    this._dataManager = new DataManager(this.siteId);
    this._api = new API(
      this.siteId,
      this.window as unknown as Window & typeof globalThis,
      this.host
    );
    this._eventHandler = new EventHandler();
    this._sdkInfo = getSdkInfo(this.startTime);

    const storedStartTime = this._dataManager.getItemOrDefault(
      KLARITICS_SESSION_START_TIME,
      startTime
    );
    this.sessionStartTime =
      typeof storedStartTime === "string"
        ? parseInt(storedStartTime)
        : Number(storedStartTime);
    this.sessionId = this.getFromStorage(KLARITICS_SESSION_ID) as string;
  }

  private _incrementLtCount = (evName: string): void => {
    if (isNotDefined(this._ltCountObj[evName])) {
      this._ltCountObj[evName] = 0;
    } else {
      this._ltCountObj[evName] += 1;
    }

    const encodedLtCount = encode(
      this.siteId,
      arrayBufferToString(
        new TextEncoder().encode(JSON.stringify(this._ltCountObj)).buffer
      )
    );
    this.persistToStorage(KLARITICS_LT_COUNT, encodedLtCount);
  };

  public reportChunkOnVisibilityChange = (): void => {
    if (!this._isInitialized) return;

    const chunk = this._prepareChunk(this._chunkDetails);
    const appId = this.siteId;
    const host = this.host;
    const payload = JSON.stringify(chunk);

    if (this._chunkDetails?.app_events.length > 0) {
      Logger.debug("Reporting a chunk on visibility change with sendBeacon");
      (this._dataManager as DataManager).removeOldChunkDetails();
      (this._dataManager as DataManager).persistOldChunk(chunk);
      navigator.sendBeacon(`${host}/v1/api/projects/${appId}/chunks`, payload);
    }
  };

  public clearNavigation = (): void => {
    this._endNavigation();
    this._currentNavigation = null;
  };

  public resetSessionProperties = (): void => {
    const sessionProperties = getDefaultSessionProperties(this.window);
    this._sessionAttributes = {};
    this.setSessionProperties(sessionProperties);
  };

  public clearLocalStorage = (): void => {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && !(klariticsKeys as string[]).includes(key)) {
        localStorage.removeItem(key);
      }
    }
  };

  public flattenJSON = (properties: Record<string, unknown>): unknown => {
    return getCleanedProperties(properties, 2);
  };

  public getDeviceId = (): string | null => {
    if (!this._isInitialized) {
      DefaultController.getClientId();
      return null;
    }
    if (!this._isRunning) {
      Logger.warn("Controller : Klaritics SDK is not running");
      return null;
    }
    return this._deviceId;
  };

  public resetUserProperties = (): void => {
    (this._dataManager as DataManager).resetUserProperties();
    const userProperties = getDefaultUserProperties(
      this.window,
      this.getDevInfo()
    );
    this._userAttributes = {};
    this.setUserProperties(userProperties);
  };

  public dispatchEvent = (
    type: string,
    event: AppEvent | SystemEvent | NavigationEvent,
    navigationInProgress: boolean = false,
    forceReport: boolean = false,
    loggedBy: string = "customer"
  ): void => {
    if (this.isTestDevice()) forceReport = true;

    this._dataManager.updateRetainedDayCount(Date.now());
    let isNewSession = false;

    const updateChunkAndNotifyListener = (): void => {
      if (isNewSession) {
        event.time = this.getTimeElapsedInSession();
      }

      if (type === APP_EVENT) {
        this._chunkDetails.app_events = [
          ...this._chunkDetails.app_events,
          event
        ];
      } else if (type === CLIENT_EVENT) {
        this.clientEvents.push(event);
        if (this.isTestDevice()) {
          this._chunkDetails.app_events = [
            ...this._chunkDetails.app_events,
            event
          ];
        }
      }

      const dispatchingEvent: AppEvent | NavigationEvent | SystemEvent = {
        ...event,
        loggedBy
      };
      this._eventHandler.notifyListeners(type, dispatchingEvent);

      if (isNewSession || type === CLIENT_EVENT) return;

      this._chunkDetails.user_attributes = { ...this._userAttributes };
      this._chunkDetails.session_attributes = { ...this._sessionAttributes };

      const chunk = this._prepareChunk(this._chunkDetails);
      this._dataManager.persistOldChunkDetails(this._chunkDetails);
      this._dataManager.persistOldChunk(chunk);

      if (forceReport || (!this._bulkUpload && !this._batchEvents)) {
        this._reportChunk(chunk, navigationInProgress);
      } else if (
        this._bulkUpload &&
        this._chunkDetails.app_events.length >= this._maxEventsSentCount
      ) {
        this._reportChunk(chunk, navigationInProgress);
      }
    };

    if (type === APP_EVENT) {
      const currentTime = Date.now();
      const lastEventTime = Number(
        this.getFromStorage(KLARITICS_LAST_EVENT_TIME)
      );
      if (
        isNumber(lastEventTime) &&
        lastEventTime > 0 &&
        Math.round(millisToSeconds(currentTime - lastEventTime)) >
          this._idleTimeout
      ) {
        this.endSession(true, () => {
          isNewSession = true;
          this.startNewSession(Date.now(), () => {
            this.persistToStorage(KLARITICS_LAST_EVENT_TIME, currentTime);
            updateChunkAndNotifyListener();
          });
        });
      } else {
        this.persistToStorage(KLARITICS_LAST_EVENT_TIME, currentTime);
        updateChunkAndNotifyListener();
      }
    } else {
      updateChunkAndNotifyListener();
    }
  };

  public endSession = (
    isForced: boolean = false,
    endcallback: () => void = () => {}
  ): void => {
    if (!this._isInitialized) {
      DefaultController.endSession();
      return;
    }

    if (!this._isRunning) {
      Logger.warn(
        "Klaritics SDK is not running. Please make sure to call 'startSession'"
      );
      return;
    }

    if (!isForced) this._endNavigation();

    const chunkDetails: ChunkDetails = {
      ...this._chunkDetails,
      user_attributes: { ...this._userAttributes },
      session_attributes: { ...this._sessionAttributes }
    };

    const chunk = this._prepareChunk(chunkDetails);
    (this._dataManager as DataManager).removeOldChunkDetails();

    const onChunkReport = (): void => {
      (this._dataManager as DataManager).clearCurrentChunk();
      (this._sessionHandler as SessionHandler).reset();
      if (
        isDefined(this._systemEventMonitor) &&
        this._isEventMonitorRegistered
      ) {
        (this._systemEventMonitor as SystemEventMonitor).unregisterEvents();
      }
      unregisterBeforeUnloadCallback(document);
      this._isRunning = false;
      endcallback();
      Logger.debug("Controller : Current session ended");
    };

    if (this._chunk502RetryTime > 0 && this._last502Time > 0) {
      (this._dataManager as DataManager).persistOldChunk(chunk);
      onChunkReport();
    } else {
      this._reportChunk(chunk, false, onChunkReport);
    }
    // this.flushAndReportVideoChunk();
  };

  public startNewSession = (
    startTime: number = Date.now(),
    startCallback: () => void = () => {},
    force: boolean = false
  ): void => {
    if (!this._isInitialized && !force) return;
    if (this._isRunning && !force) {
      startCallback();
      return;
    }

    this.sessionId = uuid();
    this.persistToStorage(KLARITICS_SESSION_ID, this.sessionId);
    this._sessionCount =
      (this.getFromStorage(REATINED_SESSIONS) as number) ?? 0;
    //Increment session count by 1
    this._sessionCount = Number(this._sessionCount) + 1;
    this.persistToStorage(REATINED_SESSIONS, this._sessionCount);
    this.sessionStartTime = startTime;
    this.persistToStorage(KLARITICS_SESSION_START_TIME, this.sessionStartTime);

    this._sessionHandler?.reset();
    this.createNewChunkAndPersist();
    this._isRunning = true;
    startCallback();
  };

  // ── Implementation of other required methods ───────────────────────────────

  setUserId(id: string): void {
    if (!this._isInitialized) return;
    this.setUserProperties({ $custom_user_id: id });
  }

  setUserProperties(properties: FieldsObject = {}): void {
    if (!this._isInitialized || !this._isRunning) return;
    this._userAttributes = { ...this._userAttributes, ...properties };
    this.persistToStorage(KLARITICS_USER_ATTR_KEY, this._userAttributes, true);
  }

  setSessionProperties(properties: FieldsObject = {}): void {
    if (!this._isInitialized || !this._isRunning) return;
    this._sessionAttributes = { ...this._sessionAttributes, ...properties };
  }

  setAppVersion(version: string): void {
    this.version = version;
  }

  setBlockChunkReportingFlag = (block: boolean): void => {
    this._blockChunkReporting = block;
  };

  // ── Storage and Utility ─────────────────────────────────────────────────────

  persistToStorage = (
    key: string,
    value: unknown,
    encodeData: boolean = false,
    storage: string = LOCAL_STORAGE
  ): void => {
    let data = value;
    if (encodeData && isObject(data)) {
      data = encode(this.siteId, JSON.stringify(data));
    }
    this._dataManager.persist(key, data as string, storage);
  };

  getFromStorage = (
    key: string,
    encoded: boolean = false,
    storage: string = LOCAL_STORAGE
  ): unknown => {
    return this._dataManager.getItem(key, encoded, storage);
  };

  getTimeElapsedInSession = (): number => {
    return millisToSeconds(Date.now() - (this.sessionStartTime ?? Date.now()));
  };

  getSDKRunningTimeInSec = (): number => {
    return millisToSeconds(Date.now() - this.startTime);
  };

  isTestDevice = (): boolean => {
    try {
      const td = this.getFromStorage("_klaritics_td", true) as
        | { id?: string }
        | null
        | undefined;
      return isDefined(td) && isDefined(td?.id);
    } catch {
      return false;
    }
  };

  // Placeholder for missing internal methods in provided snippet logic
  private _updateAppInfo = (): void => {
    Logger.debug("Controller : Updating the App info");
    const { name = "", domain = this.window.location.host } = this.config;
    const installationTime = (
      this._dataManager as DataManager
    ).getInstallationTime(this.startTime);
    const appMode = getAppMode(this.window);
    const appVersion = isDefined(this.clientConfig.version)
      ? "" + this.clientConfig.version
      : (this._dataManager as DataManager).getAppVersion();

    let appName = name as string;
    if (appName === "") {
      appName = getPageTitle(this.window);
      if (appName === "Unknown") appName = "";
    }

    this._appInfo = makeAppInfo(
      appName,
      domain as string,
      installationTime,
      appMode,
      appVersion
    );
  };

  private _postUserInfo = (): void => {
    Logger.debug("Controller : Posting user info");
    const { sdk_version } = this._sdkInfo as { sdk_version: unknown };
    const data = {
      sdk_version: JSON.stringify(sdk_version),
      app_info: this._appInfo,
      device_info: this._deviceInfo,
      user_attributes: this._userAttributes
    };
    const dataString = JSON.stringify(data as userDataPayload);
    const hash = md5(dataString);
    const oldHash = (this._dataManager as DataManager).getItemOrDefault(
      KLARITICS_USER_INFO_HASH,
      ""
    ) as string;
    if (hash === oldHash) {
      Logger.debug("Controller : No need to post user info. No change in it");
      return;
    }
    this._api.postUserInfo(data, () => {
      Logger.debug(
        "Controller : Posted user info. Persisting the hash to localstorage"
      );
      this.persistToStorage(KLARITICS_USER_INFO_HASH, hash);
    });
  };

  private _getConfigHash = (
    onConfigHashFetch: (hash: string) => void,
    onError: () => void
  ): void => {
    const existingConfig = (
      this._dataManager as DataManager
    ).getExistingConfig() as ClientConfig;
    const lastEnableDisableTime = (
      this._dataManager as DataManager
    ).getItemOrDefault(KLARITICS_LAST_ENABLE_DISABLE_TIME, -1) as number;
    const { ed_t_time } = existingConfig;

    if ((ed_t_time as number) > 0 && lastEnableDisableTime > 0) {
      const diff = Math.abs(lastEnableDisableTime - Date.now());
      const throttlingWaitInMilli = (ed_t_time as number) * 60 * 1000;

      if (diff < throttlingWaitInMilli) {
        Logger.debug(
          "Controller : Reinitializing with in the throttling time."
        );
        const lastEnabledFlag = (
          this._dataManager as DataManager
        ).getItemOrDefault(KLARITICS_LAST_ENABLED_FLAG, true) as boolean;
        if (!lastEnabledFlag) {
          Logger.debug(
            "Controller : Site is not enabled from the last update."
          );
          onError();
          return;
        }
        const hash = (this._dataManager as DataManager).getItemOrDefault(
          KLARITICS_CONFIG_HASH,
          ""
        ) as string;
        onConfigHashFetch(hash);
        return;
      }
    }
    this._checkEnableStatusFromServer(onConfigHashFetch, onError);
  };

  private _checkEnableStatusFromServer = (
    onConfigHashFetch: (hash: string) => void,
    onError: () => void
  ): void => {
    Logger.debug(
      "Controller : Getting the site enabled status from the server"
    );
    this.persistToStorage(KLARITICS_LAST_ENABLE_DISABLE_TIME, Date.now());
    this._api.checkIfSDKEnabled(
      (response: unknown) => {
        const { enabled, hash } = response as ENABLED_STATUS_API_RESPONSE;
        this.persistToStorage(KLARITICS_LAST_ENABLED_FLAG, enabled);
        if (enabled) {
          Logger.debug("Controller : Site is enabled");
          onConfigHashFetch(hash);
        } else {
          Logger.debug(
            "Controller : Site is disabled. Can't initialise the SDK"
          );
          this._isInitialized = false;
          this._isRunning = false;
          this._stopSDK();
          onError();
        }
      },
      (e: unknown) => {
        Logger.error(`Controller : enabled-status API failed ${e}`);
      }
    );
  };

  private _stopSDK = (): void => {
    Logger.debug("Controller: Stopping the SDK");
    try {
      this._isRunning = false;
      this._isInitialized = false;
      this._isSending = false;

      // DETACH REPLAY HANDLERS AND CLEAR MEMORY
      // if (this._stopVideoRecordingFn) {
      //   this._stopVideoRecordingFn();
      //   this._stopVideoRecordingFn = null;
      // }
      this._videoEventsBuffer = [];

      (this._eventHandler as EventHandler)?.tearDown();
      (this._systemEventMonitor as SystemEventMonitor)?.unregisterEvents();
    } catch (e) {
      Logger.error(`Controller : Failed to stop the SDK ${e}`);
    }
  };

  private _waitForResourceLoad = (
    configHash: string,
    onResourceWaitComplete: (hash: string) => void
  ): void => {
    Logger.debug(
      `Controller: Waiting ${this.initWaitTime}ms for all the resource loading to finish`
    );
    setTimeout(() => {
      onResourceWaitComplete(configHash);
    }, this.initWaitTime);
  };

  private _waitForBrowserStorage = (callBack: () => void): void => {
    Logger.debug(
      "Controller : Checking if the browsers localstorage is available."
    );
    Storage.waitForBrowserStorage(callBack);
  };

  private _setConfig = (): void => {
    Logger.debug("Controller : Setting the config");
    const existingConfig = (
      this._dataManager as DataManager
    ).getExistingConfig() as ClientConfig;
    this._mergeConfigWithClientConfig(existingConfig);
  };
  private _initializeFieldsFromConfig = (): void => {
    const {
      version = "",
      bulk_upload = true,
      events_upload_time,
      idle_time_out = 3600,
      max_chunk_event_count = 20,
      retry_time,
      max_sessions_in_storage = -1
    } = this.config;

    this.version = this._validateAndGetAppVersion(version as string);
    this._bulkUpload = bulk_upload as boolean;
    this._batchEvents = (events_upload_time as number) > 0;
    this._idleTimeout = isNumber(idle_time_out)
      ? (idle_time_out as number)
      : 3600;
    this._maxEventsSentCount =
      isDefined(max_chunk_event_count) && isNumber(max_chunk_event_count)
        ? (max_chunk_event_count as number)
        : 20;
    this._chunkSendInterval =
      isDefined(events_upload_time) &&
      isNumber(events_upload_time) &&
      (events_upload_time as number) >= 1000
        ? (events_upload_time as number)
        : -1;
    this._chunk502RetryTime =
      isDefined(retry_time) &&
      isNumber(retry_time) &&
      (retry_time as number) >= 1000
        ? (retry_time as number)
        : -1;
    this._maxSessionsInStorage =
      isNumber(max_sessions_in_storage) &&
      (max_sessions_in_storage as number) > 0
        ? (max_sessions_in_storage as number)
        : -1;
  };

  private _validateAndGetAppVersion = (app_version: string): string => {
    if (isDefined(app_version)) {
      (this._dataManager as DataManager).persistAppVersion(app_version);
      return app_version;
    }
    const savedAppVersion = (
      this._dataManager as DataManager
    ).getAppVersion() as string | undefined;
    return isDefined(savedAppVersion) ? savedAppVersion! : "";
  };

  private _checkEnableStatusInConfig = (
    enabledCallback: () => void,
    disabledCallback: () => void
  ): void => {
    Logger.debug(
      "Controller : Checking if the Site is enabled existing config"
    );
    if (!this.config.enabled) {
      Logger.debug("Controller : Site is disabled. Can't initialise");
      this._downloadConfig("", () => {});
      this._postUserInfo();
      disabledCallback();
      return;
    }
    enabledCallback();
  };

  private _initializeLTCount = (): void => {
    Logger.debug("Controller : Initializing the LT Count ");
    try {
      const klaritics_lt_countObj = this.getFromStorage(KLARITICS_LT_COUNT) as
        | string
        | null;
      if (klaritics_lt_countObj) {
        this._ltCountObj = JSON.parse(
          new TextDecoder().decode(
            stringToArrayBuffer(decode(this.siteId, klaritics_lt_countObj))
          )
        ) as LtCountMap;
      } else {
        this._ltCountObj = {};
        const encoded = encode(
          this.siteId,
          arrayBufferToString(
            new TextEncoder().encode(JSON.stringify(this._ltCountObj)).buffer
          )
        );
        this.persistToStorage(KLARITICS_LT_COUNT, encoded);
      }
    } catch (e) {
      Logger.error(`Controller : LT Count object parsing failed ${e}`);
    }
  };

  private _getDeviceId = (): string => {
    let deviceId = (this._dataManager as DataManager).getUserId() as
      | string
      | undefined;
    if (!isDefined(deviceId)) {
      Logger.debug("Controller : Device id not found in localstorage");
      const { client_id } = this.config;
      if (isDefined(client_id) && client_id !== "") {
        Logger.debug(
          "Controller : client id from clientconfig is the deviceid"
        );
        deviceId = client_id as string;
      } else {
        Logger.debug(
          "Controller : Generated a new device id. This is a new device now"
        );
        deviceId = uuid();
      }
      this._setNewUserFlags(deviceId);
    }
    Logger.debug(`Controller : Device id is ${deviceId}`);
    this._deviceId = deviceId!;
    return deviceId!;
  };

  private _setNewUserFlags = (deviceId: string): void => {
    (this._dataManager as DataManager).clearStorage();
    (this._dataManager as DataManager).persistUserId(deviceId);
    (this._dataManager as DataManager).persistInstallationTime(Date.now());
    this._sessionInfo = { ...this._sessionInfo, is_first_session: true };
    this._isFirstSession = true;
  };

  private _setDeviceInfo = (): void => {
    const {
      hardware_model,
      os_version,
      $browser_version,
      $browser,
      dimensions,
      dpi,
      dimensions_in_pixels
    } = getDeviceInfo(this.window);
    this._deviceInfo = makeDeviceInfo(
      this._deviceId,
      hardware_model!,
      dimensions!,
      os_version!,
      dpi!,
      dimensions_in_pixels!,
      $browser!,
      $browser_version!
    );

    getGeoLocation()
      .then((location) => {
        if (location) {
          this._deviceInfo.$location = location;
        }
      })
      .catch((error) => {
        Logger.error(`Controller : Failed to resolve geolocation : ${error}`);
      });
  };

  private _setDefaultUserProperties = (): void => {
    Logger.debug("Controller : Setting the default User attributes");
    const userProperties = getDefaultUserProperties(this.window, {
      ...this._deviceInfo
    });
    this._userAttributes = { ...this._userAttributes, ...userProperties };
  };

  private _setDefaultSessionProperties = (): void => {
    Logger.debug("Controller : Setting the default session attributes");
    const sessionProperties = getDefaultSessionProperties(this.window);
    this._sessionAttributes = {
      ...this._sessionAttributes,
      ...sessionProperties
    };
  };

  private _mergeStoredAndDefaultUserAttributes = (): void => {
    Logger.debug("Controller : Merging the stored and default user attributes");
    const storedAttributes = this.getFromStorage(
      KLARITICS_USER_ATTR_KEY,
      true
    ) as string | null;
    if (isDefined(storedAttributes)) {
      let decodedAttributes = getJSONOrNull(storedAttributes!) as Record<
        string,
        unknown
      > | null;
      if (decodedAttributes === null) {
        try {
          decodedAttributes = JSON.parse(
            decode(this.siteId, storedAttributes!)
          ) as Record<string, unknown>;
        } catch {
          decodedAttributes = {};
        }
      }
      this._userAttributes = {
        ...(decodedAttributes ?? {}),
        ...this._userAttributes
      };
    }
  };

  private _getStoredListOfEventsLogged = (): void => {
    const raw = this.getFromStorage(KLARITICS_EVENTS_LOGGED, true) as
      | string
      | null;
    if (isDefined(raw)) {
      let decoded = getJSONOrNull(raw!) as EventsLoggedMap | null;
      if (decoded === null) {
        try {
          decoded = JSON.parse(decode(this.siteId, raw!)) as EventsLoggedMap;
        } catch {
          decoded = {} as EventsLoggedMap;
        }
      }
      this._eventsLogged = { ...(decoded ?? {}) };
    }
  };

  private _setInitialSessionState = (): void => {
    Logger.debug(
      `Controller : Setting the session state for session id ${this.sessionId}`
    );
    if (!this.sessionId) {
      this.newSession = true;
      this.sessionId = uuid();
      this.persistToStorage(KLARITICS_SESSION_ID, this.sessionId);
      this.persistToStorage(
        KLARITICS_SESSION_START_TIME,
        this.sessionStartTime
      );
    }
  };

  private _reinitialiseChunkDetails = (): void => {
    this._chunkDetails = {
      ...DEFAULT_CHUNK_DETAILS,
      user_attributes: { ...this._userAttributes },
      session_attributes: { ...this._sessionAttributes }
    };
    (this._dataManager as DataManager).persistOldChunkDetails(
      this._chunkDetails
    );
  };

  private _addAndClearUnsentEvents = (): void => {
    const unsentChunkDetails = (
      this._dataManager as DataManager
    ).getOldChunkDetails() as ChunkDetails | null;
    if (isDefined(unsentChunkDetails) && unsentChunkDetails !== null) {
      this._chunkDetails.app_events = [
        ...(this._chunkDetails.app_events ?? []),
        ...unsentChunkDetails.app_events
      ];
      this._chunkDetails.navigation_events = [
        ...(this._chunkDetails.navigation_events ?? []),
        ...unsentChunkDetails.navigation_events
      ];
      this._chunkDetails.system_events = [
        ...(this._chunkDetails.system_events ?? []),
        ...unsentChunkDetails.system_events
      ];
      this._chunkDetails.user_events = [
        ...(this._chunkDetails.user_events ?? []),
        ...unsentChunkDetails.user_events
      ];
      (this._dataManager as DataManager).removeOldChunkDetails();
    }
  };

  private _canFetchConfig = (hash: string): boolean => {
    if (hash === "") return true;
    const oldHash = (this._dataManager as DataManager).getItemOrDefault(
      KLARITICS_CONFIG_HASH,
      ""
    ) as string;
    return oldHash !== hash;
  };

  private _mergeConfigWithClientConfig = (
    newConfig: ClientConfig | Details | undefined
  ): void => {
    this.config = { ...newConfig, ...this.clientConfig };
    Logger.debug("Controller : New config and client configs merged");
  };

  private _downloadConfig = (
    hash: string,
    completionCallback: () => void
  ): void => {
    Logger.debug("Controller : Fetch a new config");
    // Helper to ensure blacklist is applied and process continues
    const finalize = (configData: Details | undefined) => {
      this._initializeBlackListProperties(configData);
      completionCallback();
    };
    if (!this._canFetchConfig(hash)) {
      Logger.debug("Controller : No need to fetch config. No change in hash");
      // If old hash and current hash matches, then no need to fetch config
      finalize(this.config as Details);
      return;
    }
    const configThrottleTime = this.config.sct_time as number;
    const lastConfigFetchTime = (
      this._dataManager as DataManager
    ).getItemOrDefault(KLARITICS_LAST_CONFIG_TIME, -1) as number;
    if (configThrottleTime > 0 && lastConfigFetchTime > 0) {
      const diff = Math.abs(Date.now() - lastConfigFetchTime);
      if (diff < configThrottleTime) {
        Logger.debug(
          "Controller : No need to fetch config. Did not pass the throttle time"
        );
        return;
      }
    }
    this.persistToStorage(KLARITICS_LAST_CONFIG_TIME, Date.now());
    this._api.getConfig(
      (response: unknown) => {
        Logger.debug("Controller : New config fetched");
        const details = response as Details;
        (this._dataManager as DataManager).persistConfig(details);
        if (details && typeof details.hash !== "undefined") {
          this.persistToStorage(KLARITICS_CONFIG_HASH, details.hash);
        } else {
          Logger.debug("Controller : Config details missing hash");
        }
        this._mergeConfigWithClientConfig(details);
        this._updateAppInfo();
        finalize(details);
      },
      (error) => {
        Logger.error("Controller : Failed to fetch config", error);
      }
    );
  };
  private _getTotalBackgroundTime = (): number => {
    const { total_duration = 0, duration = 0 } = this._sessionInfo;
    return (total_duration as number) > (duration as number)
      ? (total_duration as number) - (duration as number)
      : 0;
  };
  private _registerSystemEventMonitor = (): void => {
    Logger.debug("Registering a handler for the system events");
    executeIfDOMReady(
      this.window as unknown as Window & typeof globalThis,
      () => {
        if (!this._isEventMonitorRegistered) {
          this._executeBeforeUnload();
          this._systemEventMonitor = new SystemEventMonitor(false);
          this._isEventMonitorRegistered = true;
        }
      }
    );
  };

  private _executeBeforeUnload = (): void => {
    let isCalled = false;
    executeBeforeUnload(window, () => {
      Logger.debug("Controller : Executing before unload");
      if (isCalled || !this._isRunning) return;
      isCalled = true;
      const isForeground = window.document.visibilityState === VISIBLE;
      if (isForeground) {
        this._endNavigation(true);
        this._dispatchSystemEvent("hidden");
      }
      if (this._sendChunkOnEventsId !== null)
        clearInterval(this._sendChunkOnEventsId);
      if (this._sendChunkOnETAId !== null)
        clearInterval(this._sendChunkOnETAId);
      if (this._bulkUpload) {
        (this._dataManager as DataManager).persistOldChunkDetails(
          this._chunkDetails
        );
      }
      (this._systemEventMonitor as SystemEventMonitor).unregisterEvents();
    });
  };

  sendOldChunk = (onOldChunkReport: () => void = () => {}): void => {
    Logger.debug("Controller : Sending the old chunk to the server");
    let oldChunk = (
      this._dataManager as DataManager
    ).getOldChunk() as Chunk | null;

    if (isDefined(oldChunk) && oldChunk !== null) {
      const chunkDetails = { ...this._chunkDetails };
      this._chunkDetails = { ...(oldChunk.chunk_details as ChunkDetails) };

      if (!(this._chunkDetails?.app_events?.length > 0)) {
        this._addAndClearUnsentEvents();
      }

      if (!(this._chunkDetails?.app_events?.length > 0)) {
        this._chunkDetails = { ...chunkDetails };
        onOldChunkReport();
        return;
      }

      oldChunk = { ...oldChunk, chunk_details: { ...this._chunkDetails } };

      const oldChunkSessionId: string =
        (oldChunk?.session_info?.id as string) ?? "1";
      const oldChunkNumber: number =
        (oldChunk?.chunk_meta?.number as number) ?? 1;
      (this._dataManager as DataManager).persistCurrentChunk(oldChunk);

      this._api.postChunk(
        oldChunk as Chunk,
        () => {
          this._updateChunkNumberMap(oldChunkSessionId, oldChunkNumber + 1);
          Logger.debug("Controller : Old chunk reporting successful.");
          (this._dataManager as DataManager).removeOldChunk();
          (this._dataManager as DataManager).removeOldChunkDetails();
          this._reinitialiseChunkDetails();
          this.createNewChunkAndPersist();
          onOldChunkReport();
        },
        () => {
          Logger.debug("Controller : Old chunk reporting failed.");
          onOldChunkReport();
        }
      );
    } else {
      onOldChunkReport();
    }
  };

  private _updateChunkNumberMap = (
    sessionId: string,
    chunkNumber: number
  ): void => {
    const sessionChunkMap = (
      this._dataManager as DataManager
    ).getSessionsChunkNumberMap() as Record<string, number>;
    sessionChunkMap[sessionId] = chunkNumber;
    (this._dataManager as DataManager).persistSessionsChunkNumberMap(
      sessionChunkMap
    );
    this._chunkNumber = chunkNumber;
  };

  createNewChunkAndPersist = (): Chunk => {
    const chunk = this.createANewChunk();
    (this._dataManager as DataManager).persistCurrentChunk(chunk);
    return chunk;
  };

  dispatchValidationEvent = (
    name: string,
    additionalInfo?: Record<string, unknown>
  ): void => {
    if (!this._isInitialized || !this._isRunning) {
      Logger.warn("Controller : Can't log client event. SDK not initialised");
      DefaultController.dispatchValidationEvent();
      return;
    }
    const custom_event = new CustomEvent("antValidationEvent", {
      detail: { name, additionalInfo }
    });
    window.dispatchEvent(custom_event);
  };

  createANewChunk = (): Chunk => {
    const app_name = isDefined(this.config.name)
      ? (this.config.name as string)
      : getPageTitle(this.window);
    const bundle_id = isDefined(this.config.domain)
      ? (this.config.domain as string)
      : "";

    const data = (this._dataManager as DataManager).getNewChunk(
      this.startTime,
      this.sessionStartTime!,
      this.sessionId,
      this._idleTimeout
    );
    const chunk = data.chunk as Chunk;

    const { session_info, app_info, sdk_info, chunk_details, chunk_meta } =
      chunk;
    const { session_attributes, user_attributes } = chunk_details;

    this._sessionInfo = { ...this._sessionInfo, ...session_info };
    this._sessionInfo.is_first_session = this._isFirstSession;
    this._sdkInfo = sdk_info as SDKInfo;
    this._appInfo = {
      ...app_info,
      app_name,
      bundle_id,
      app_version: "" + this.version
    };
    this._chunkNumber = chunk_meta.number;
    this._userAttributes = {
      ...this._userAttributes,
      ...(user_attributes as Record<string, unknown>)
    };
    this._sessionAttributes = {
      ...this._sessionAttributes,
      ...(session_attributes as Record<string, unknown>)
    };
    this._chunkDetails = chunk_details;
    this._addAndClearUnsentEvents();

    this.newSession = data.is_new_session as boolean;
    if (!this.newSession) this._setSessionIdFromChunk(chunk);

    return {
      sdk_info: this._sdkInfo,
      session_info: this._sessionInfo,
      app_info: this._appInfo,
      device_info: this._deviceInfo,
      chunk_details,
      chunk_meta
    };
  };

  private _setSessionIdFromChunk = (chunk: Chunk): void => {
    const id = chunk?.session_info?.id as string | undefined;
    if (id) {
      this.sessionId = id;
      this.persistToStorage(KLARITICS_SESSION_ID, this.sessionId);
    }
  };

  private _sendChunkWithAtleastOneEvent = (): void => {
    const { app_events, navigation_events } = this._chunkDetails;
    const eventsCount = app_events.length + navigation_events.length;
    if (eventsCount > 0 && !this._blockChunkReporting) {
      this._prepareAndReportChunk();
    }
  };

  private _prepareAndReportChunk = (): void => {
    Logger.debug("Controller : Preparing and reporting chunk");
    const chunk = this._prepareChunk(this._chunkDetails);
    this._reportChunk(chunk, false);
  };

  private _registerIntervalBasedChunkReporting = (): void => {
    Logger.debug("Controller : Registering the handlers for chunk reporting");
    if (this._sendChunkOnETAId !== null) clearInterval(this._sendChunkOnETAId);

    this._sendChunkOnETAId = setInterval(() => {
      this._sendChunkWithAtleastOneEvent();

      // SINK STREAMING ENGINE TIMELINE SYNC
      // this.flushAndReportVideoChunk();
    }, this._internalChunkSendInterval);

    if (this._batchEvents) {
      if (this._sendChunkOnEventsId !== null)
        clearInterval(this._sendChunkOnEventsId);
      this._sendChunkOnEventsId = setInterval(() => {
        this._sendChunkWithMaxedOutEvents();
      }, this._chunkSendInterval);
    }
  };

  private _sendChunkWithMaxedOutEvents = (): void => {
    const { app_events, navigation_events } = this._chunkDetails;
    const eventsCount = app_events.length + navigation_events.length;
    if (
      (!this._bulkUpload && eventsCount > 0) ||
      (this._bulkUpload && eventsCount >= this._maxEventsSentCount)
    ) {
      this._prepareAndReportChunk();
    }
  };

  private _dispatchSystemEvent = (visibilityState: string): void => {
    Logger.debug("Controller : Dispatching SYSTEM_EVENT");
    const systemEvent = makeSystemEvent(
      visibilityState,
      this.getTimeElapsedInSession()
    );
    this.dispatchEvent(SYSTEM_EVENT, systemEvent);
  };
  private _endNavigation = (navigationInProgress: boolean = false): void => {
    Logger.debug("Controller : Ending the navigation");
    if (
      isDefined(this._currentNavigation) &&
      this._currentNavigation !== null
    ) {
      this._currentNavigation.duration = toDecimalPoints(
        this.getSDKRunningTimeInSec() -
          (this._currentNavigation.transition_time ?? 0)
      );
      this.dispatchEvent(
        NAVIGATION_EVENT,
        { ...this._currentNavigation },
        navigationInProgress
      );
    }
  };

  private _updateListOfEventsLogged = (
    name: string,
    additionalInfo: Record<string, unknown>
  ): void => {
    this._eventsLogged[name] = this._eventsLogged[name] ?? {
      first_logged_time: new Date().toISOString()
    };
    this._eventsLogged[name] = {
      ...this._eventsLogged[name],
      ...additionalInfo,
      latest_logged_time: new Date().toISOString()
    };
    this.persistToStorage(
      KLARITICS_EVENTS_LOGGED,
      encode(this.siteId, JSON.stringify(this._eventsLogged))
    );
  };

  private _prepareChunk = (
    chunkDetails: ChunkDetails = {} as ChunkDetails
  ): Chunk => {
    Logger.debug("Controller : Preparing chunk");
    this._updateDuration();

    const chunkMeta: ChunkMeta = {
      id: this.sessionId + ":" + this._chunkNumber,
      number: this._chunkNumber,
      reporting_times: [Date.now()]
    };

    this._userAttributes = ignoreUserProps(
      this._stopUserPropsFromConfig,
      this._userAttributes
    );
    // this._sessionAttributes = ignoreSessionProps(
    //   this._stopSessionPropsFromConfig,
    //   this._sessionAttributes
    // );

    chunkDetails = {
      ...chunkDetails,
      user_attributes: this._userAttributes,
      session_attributes: this._sessionAttributes
    };

    return {
      sdk_info: this._sdkInfo,
      session_info: this._sessionInfo,
      app_info: this._appInfo,
      device_info: this._deviceInfo,
      chunk_meta: chunkMeta,
      chunk_details: chunkDetails
    };
  };

  private _updateDuration = (): void => {
    Logger.debug("Controller : Updating the foreground time");
    const duration = this.getSDKRunningTimeInSec();
    const totalBgTime = (
      this._sessionHandler as SessionHandler
    ).getTotalBackgroundTime();
    const totalDuration = duration;
    this._sessionInfo = {
      ...this._sessionInfo,
      duration: toDecimalPoints(duration - totalBgTime),
      total_duration: toDecimalPoints(totalDuration)
    };
  };

  private _reportChunk = (
    chunk: Chunk,
    navigationInProgress: boolean = false,
    callback: () => void = () => {}
  ): void => {
    Logger.debug("Controller : Reporting chunk");
    if (this._isSending) {
      this._pendingChunk = true;
      return;
    }
    if (
      this._chunk502RetryTime > 0 &&
      this._last502Time !== -1 &&
      Date.now() - this._last502Time < this._chunk502RetryTime
    ) {
      this._reinitialiseChunkDetails();
      return;
    }

    const { app_events, system_events, navigation_events, user_events } =
      chunk.chunk_details;
    if (
      app_events.length < 1 &&
      system_events.length < 1 &&
      navigation_events.length < 1 &&
      user_events.length < 1
    ) {
      Logger.debug("Controller : Chunk not sent. No events in the chunk");
      this._isSending = false;
      callback();
      return;
    }

    this._isSending = true;
    this._updateChunkNumberMap(this._sessionInfo.id!, this._chunkNumber + 1);
    (this._dataManager as DataManager).removeOldChunkDetails();
    (this._dataManager as DataManager).removeOldChunk();
    this._reinitialiseChunkDetails();

    if (!navigationInProgress) {
      (this._dataManager as DataManager).persistCurrentChunk(chunk);
      this._api.postChunk(
        chunk,
        () => {
          Logger.debug("Controller : Chunk successfully sent");
          this._isSending = false;
          this._last502Time = -1;
          callback();
          if (this._pendingChunk) {
            this._pendingChunk = false;
            const nextChunk = this._prepareChunk(this._chunkDetails);
            this._reportChunk(nextChunk);
          }
        },
        (errorCode: unknown) => {
          Logger.debug("Controller : Chunk sending failed");
          if (errorCode === 0) return;
          this._isSending = false;
          if ((errorCode as number) >= 500) this._last502Time = Date.now();
          if (errorCode !== 413 && errorCode !== 417) {
            (this._dataManager as DataManager).persistOldChunk(chunk);
          }
          callback();
        }
      );
    } else {
      this._isSending = false;
      (this._dataManager as DataManager).persistOldChunk(chunk);
      callback();
    }
  };
}
